(() => {
  const ROOT_SELECTOR = "[data-ns-theory-root]";
  const initialized = new WeakSet();
  const cleanups = new WeakMap();

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const pad = (value) => String(value).padStart(2, "0");

  function initialize(root) {
    if (!root || initialized.has(root)) return;
    initialized.add(root);

    const cleanupTasks = [];
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const saveData = Boolean(connection && connection.saveData);

    root.classList.toggle("is-data-saver", saveData);
    root.classList.toggle("is-static", reduceMotion.matches);

    setupTechnology(root, {
      disabled: saveData || reduceMotion.matches,
      onCleanup: (task) => cleanupTasks.push(task),
    });
    setupCarousel(root, (task) => cleanupTasks.push(task));

    const handleMotionChange = () => {
      queueMicrotask(() => {
        cleanups.get(root)?.();
        initialize(root);
      });
    };
    reduceMotion.addEventListener?.("change", handleMotionChange);
    cleanupTasks.push(() => reduceMotion.removeEventListener?.("change", handleMotionChange));

    cleanups.set(root, () => {
      cleanupTasks.forEach((task) => task());
      cleanups.delete(root);
      initialized.delete(root);
    });
  }

  function setupTechnology(root, { disabled, onCleanup }) {
    const section = root.querySelector("[data-ns-theory-technology]");
    const canvas = root.querySelector("[data-ns-theory-canvas]");
    const frameData = root.querySelector("[data-ns-theory-frames]");
    const features = [...root.querySelectorAll("[data-ns-theory-feature]")];
    const progress = root.querySelector("[data-ns-theory-progress]");

    if (!section || !canvas || !frameData || !features.length) return;

    if (disabled) {
      features.forEach((feature) => {
        feature.classList.add("is-active");
        feature.removeAttribute("aria-hidden");
      });
      return;
    }

    let urls;
    try {
      urls = JSON.parse(frameData.textContent);
    } catch (error) {
      root.classList.add("is-static");
      return;
    }

    if (!Array.isArray(urls) || urls.length !== 181) {
      root.classList.add("is-static");
      return;
    }

    const context = canvas.getContext("2d", { alpha: false, desynchronized: true });
    if (!context) {
      root.classList.add("is-static");
      return;
    }

    const frames = new Array(urls.length);
    const requested = new Set();
    let active = false;
    let destroyed = false;
    let rafId = 0;
    let targetFrame = 0;
    let drawnFrame = -1;
    let lastFeature = -1;
    let canvasWidth = 0;
    let canvasHeight = 0;
    let idleHandle = 0;

    const nearestLoadedFrame = (target) => {
      if (frames[target]) return target;
      for (let offset = 1; offset < frames.length; offset += 1) {
        const below = target - offset;
        const above = target + offset;
        if (below >= 0 && frames[below]) return below;
        if (above < frames.length && frames[above]) return above;
      }
      return -1;
    };

    const resizeCanvas = () => {
      const ratio = Math.min(window.devicePixelRatio || 1, 1.5);
      const bounds = canvas.getBoundingClientRect();
      const nextWidth = Math.max(1, Math.round(bounds.width * ratio));
      const nextHeight = Math.max(1, Math.round(bounds.height * ratio));
      if (nextWidth === canvasWidth && nextHeight === canvasHeight) return;
      canvasWidth = nextWidth;
      canvasHeight = nextHeight;
      canvas.width = nextWidth;
      canvas.height = nextHeight;
      drawnFrame = -1;
    };

    const draw = (index) => {
      const loadedIndex = nearestLoadedFrame(index);
      if (loadedIndex < 0 || loadedIndex === drawnFrame) return;
      const image = frames[loadedIndex];
      const scale = Math.min(canvasWidth / image.naturalWidth, canvasHeight / image.naturalHeight);
      const width = image.naturalWidth * scale;
      const height = image.naturalHeight * scale;
      const x = (canvasWidth - width) / 2;
      const y = (canvasHeight - height) / 2;
      context.fillStyle = "#000";
      context.fillRect(0, 0, canvasWidth, canvasHeight);
      context.drawImage(image, x, y, width, height);
      drawnFrame = loadedIndex;
    };

    const requestFrame = (index, priority = false) => {
      if (destroyed || requested.has(index) || index < 0 || index >= urls.length) return;
      requested.add(index);
      const image = new Image();
      image.decoding = "async";
      if (priority) image.fetchPriority = "high";
      image.onload = async () => {
        try {
          await image.decode?.();
        } catch (error) {
          // The loaded bitmap remains drawable even when decode() is unsupported.
        }
        if (destroyed) return;
        frames[index] = image;
        if (active && Math.abs(index - targetFrame) <= 3) scheduleRender();
      };
      image.onerror = () => requested.delete(index);
      image.src = urls[index];
    };

    const requestNearby = (center) => {
      requestFrame(center, true);
      for (let offset = 1; offset <= 7; offset += 1) {
        requestFrame(center + offset, offset <= 2);
        requestFrame(center - offset, offset <= 2);
      }
    };

    const updateFeature = (progressValue) => {
      const nextFeature = clamp(Math.floor(progressValue * features.length), 0, features.length - 1);
      if (nextFeature === lastFeature) return;
      features.forEach((feature, index) => {
        const selected = index === nextFeature;
        feature.classList.toggle("is-active", selected);
        feature.setAttribute("aria-hidden", selected ? "false" : "true");
      });
      lastFeature = nextFeature;
    };

    const render = () => {
      rafId = 0;
      if (!active || destroyed) return;
      resizeCanvas();
      const bounds = section.getBoundingClientRect();
      const scrollable = Math.max(1, section.offsetHeight - window.innerHeight);
      const progressValue = clamp(-bounds.top / scrollable, 0, 1);
      targetFrame = Math.round(progressValue * (urls.length - 1));
      requestNearby(targetFrame);
      draw(targetFrame);
      updateFeature(progressValue);
      if (progress) progress.style.transform = `scaleY(${progressValue})`;
    };

    const scheduleRender = () => {
      if (!rafId) rafId = requestAnimationFrame(render);
    };

    const onScroll = () => scheduleRender();
    const onResize = () => {
      drawnFrame = -1;
      scheduleRender();
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        active = entry.isIntersecting;
        if (active) {
          scheduleRender();
        } else if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = 0;
        }
      },
      { rootMargin: "35% 0px" },
    );
    observer.observe(section);

    requestFrame(0, true);
    requestFrame(1, true);
    requestFrame(180, true);

    let preloadCursor = 2;
    const preloadBatch = (deadline) => {
      if (destroyed) return;
      let loadedThisBatch = 0;
      while (
        preloadCursor < urls.length - 1 &&
        loadedThisBatch < 5 &&
        (!deadline || deadline.timeRemaining() > 4 || deadline.didTimeout)
      ) {
        requestFrame(preloadCursor);
        preloadCursor += 1;
        loadedThisBatch += 1;
      }
      if (preloadCursor < urls.length - 1) {
        if ("requestIdleCallback" in window) {
          idleHandle = window.requestIdleCallback(preloadBatch, { timeout: 1400 });
        } else {
          idleHandle = window.setTimeout(() => preloadBatch(), 120);
        }
      }
    };

    if ("requestIdleCallback" in window) {
      idleHandle = window.requestIdleCallback(preloadBatch, { timeout: 1000 });
    } else {
      idleHandle = window.setTimeout(() => preloadBatch(), 150);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize, { passive: true });

    onCleanup(() => {
      destroyed = true;
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      if (rafId) cancelAnimationFrame(rafId);
      if ("cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleHandle);
      } else {
        window.clearTimeout(idleHandle);
      }
    });
  }

  function setupCarousel(root, onCleanup) {
    const carousel = root.querySelector("[data-ns-theory-carousel]");
    if (!carousel) return;

    const slides = [...carousel.querySelectorAll("[data-ns-theory-slide]")];
    const previous = carousel.querySelector("[data-ns-theory-previous]");
    const next = carousel.querySelector("[data-ns-theory-next]");
    const current = carousel.querySelector("[data-ns-theory-current]");
    const total = carousel.querySelector("[data-ns-theory-total]");
    const consoleStatus = carousel.querySelector("[data-ns-theory-console-status]");
    const jumpControls = [...carousel.querySelectorAll("[data-ns-theory-jump]")];
    if (!slides.length || !previous || !next) return;

    let index = 0;
    let touchStartX = null;

    if (total) total.textContent = pad(slides.length);

    const show = (requestedIndex) => {
      index = (requestedIndex + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        const selected = slideIndex === index;
        slide.classList.toggle("is-active", selected);
        slide.setAttribute("aria-hidden", selected ? "false" : "true");
        slide.querySelectorAll("a, button").forEach((control) => {
          if (selected) {
            control.removeAttribute("tabindex");
          } else {
            control.setAttribute("tabindex", "-1");
          }
        });
      });
      if (current) current.textContent = pad(index + 1);
      if (consoleStatus) consoleStatus.textContent = `Archive ${pad(index + 1)} selected`;
      jumpControls.forEach((control, controlIndex) => {
        const selected = controlIndex === index;
        control.classList.toggle("is-active", selected);
        control.setAttribute("aria-pressed", selected ? "true" : "false");
      });
    };

    const goPrevious = () => show(index - 1);
    const goNext = () => show(index + 1);
    const onKeydown = (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        goPrevious();
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        goNext();
      }
    };
    const onTouchStart = (event) => {
      touchStartX = event.changedTouches[0]?.clientX ?? null;
    };
    const onTouchEnd = (event) => {
      if (touchStartX === null) return;
      const touchEndX = event.changedTouches[0]?.clientX ?? touchStartX;
      const delta = touchEndX - touchStartX;
      touchStartX = null;
      if (Math.abs(delta) < 45) return;
      if (delta > 0) goPrevious();
      else goNext();
    };

    previous.addEventListener("click", goPrevious);
    next.addEventListener("click", goNext);
    carousel.addEventListener("keydown", onKeydown);
    carousel.addEventListener("touchstart", onTouchStart, { passive: true });
    carousel.addEventListener("touchend", onTouchEnd, { passive: true });
    const jumpHandlers = jumpControls.map((control) => {
      const handler = () => show(Number(control.dataset.nsTheoryJump || 0));
      control.addEventListener("click", handler);
      return [control, handler];
    });
    show(0);

    onCleanup(() => {
      previous.removeEventListener("click", goPrevious);
      next.removeEventListener("click", goNext);
      carousel.removeEventListener("keydown", onKeydown);
      carousel.removeEventListener("touchstart", onTouchStart);
      carousel.removeEventListener("touchend", onTouchEnd);
      jumpHandlers.forEach(([control, handler]) => control.removeEventListener("click", handler));
    });
  }

  function initializeAll(container = document) {
    if (container.matches?.(ROOT_SELECTOR)) initialize(container);
    container.querySelectorAll?.(ROOT_SELECTOR).forEach(initialize);
  }

  function unload(container) {
    const root = container.matches?.(ROOT_SELECTOR)
      ? container
      : container.querySelector?.(ROOT_SELECTOR);
    cleanups.get(root)?.();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => initializeAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => unload(event.target));
})();
